import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  calculatePayrollPreview,
  getPayrollPayment,
  getPayrollPayments,
  getCompanyLive,
  getEmployeeLive,
  getMyLive,
  payPayrollSelection,
  reversePayrollPayment,
} from '@/api/services/payroll-live.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  PayrollCalculateRequest,
  PayrollPayRequest,
} from '@/features/payroll/types/payroll.types'

export const payrollLiveKeys = {
  company: (companyId: string | null) =>
    ['payroll-live', 'company', companyId] as const,
  employee: (companyId: string | null, employeeId: string | null) =>
    ['payroll-live', 'employee', companyId, employeeId] as const,
  me: () => ['payroll-live', 'me'] as const,
  payments: (params?: { page?: number; limit?: number; year?: number; month?: number }) =>
    ['payroll-payments', params] as const,
  payment: (snapshotId: string | null) => ['payroll-payments', snapshotId] as const,
}

/** Refresh cadence for live estimates — a minute keeps "live" honest without hammering the API. */
const LIVE_REFETCH_MS = 60_000

export function useCompanyLive(
  companyId: string | null,
  options?: { enabled?: boolean },
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollLiveKeys.company(companyId),
    queryFn: () => getCompanyLive(companyId!),
    staleTime: 30_000,
    refetchInterval: LIVE_REFETCH_MS,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function useEmployeeLive(
  companyId: string | null,
  employeeId: string | null,
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollLiveKeys.employee(companyId, employeeId),
    queryFn: () => getEmployeeLive(companyId!, employeeId!),
    staleTime: 30_000,
    refetchInterval: LIVE_REFETCH_MS,
    enabled: !!companyId && !!employeeId,
  })
}

export function useMyLive() {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_self',
    queryKey: payrollLiveKeys.me(),
    queryFn: () => getMyLive(),
    staleTime: 30_000,
    refetchInterval: LIVE_REFETCH_MS,
  })
}

export function useCalculatePayrollPreview() {
  return useMutation({
    mutationFn: (body: PayrollCalculateRequest) => calculatePayrollPreview(body),
    onSuccess: (result) => {
      toast.success(
        `Calculated preview for ${result.totals.readyCount} ready employees.`,
      )
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function usePayPayrollSelection() {
  return useMutation({
    mutationFn: (body: PayrollPayRequest) => payPayrollSelection(body),
    onSuccess: (result) => {
      toast.success(
        `Paid ${result.snapshot.employeeCount} employee(s). Salary slip records created.`,
      )
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function usePayrollPayments(params?: {
  page?: number
  limit?: number
  year?: number
  month?: number
}) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: payrollLiveKeys.payments(params),
    queryFn: () => getPayrollPayments(params),
    staleTime: 30_000,
  })
}

export function usePayrollPayment(snapshotId: string | null) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: payrollLiveKeys.payment(snapshotId),
    queryFn: () => getPayrollPayment(snapshotId!),
    staleTime: 30_000,
    enabled: !!snapshotId,
  })
}

export function useReversePayrollPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ snapshotId, reason }: { snapshotId: string; reason: string }) =>
      reversePayrollPayment(snapshotId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] })
      // Reversal reopens the month: live estimates and batch views change too.
      qc.invalidateQueries({ queryKey: ['payroll-live'] })
      qc.invalidateQueries({ queryKey: ['payroll-batches'] })
      toast.success('Payment reversed. The period is open again.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
